import { isEmpty } from 'lodash';
import { PRECARE_SEARCH_SCENARIO, SEARCH_SCENARIOS } from '../../../../../../common/constants/strategy-search-constant';
import { AppSession } from '../../../../../../common/values/appSession';
import { ISearchParameters } from '../../../../../../fad/search-providers/interfaces/iSearchParameters';
import { MEDICAL_COVERAGETYPE, PROVIDER_TYPE, VISITS } from '../../../../../../fad/search-providers/values/providerSearchConstants';
import { AMPERSAND, DEEP_LINK_CMP, DEEP_LINK_ID, DOT_OPERAND } from '../../../../../../fad/search-results/values/providerSearchConstants';
import { ResultsUtil } from '../../../../../../fad/utilities/resultsUtil';
import { NETWORK_TYPE, PageAlertType, PageAlertTypeStyle, SearchExecutionMode } from '../../../../../../findcare/common/constants/common';
import { ALERTS_TO_BE_SUPPRESSED, ALERT_CUSTOMER_SUPPORT_BRAND_URL_CONFIG, PAGE_ALERT_TYPE } from '../../../../../../findcare/common/contents/common/alertTypeConfig';
import { FindCarePageType } from '../../../../../../findcare/common/enums/findCarePages';
import { IPageAlertRequest, IPageAlertResponse, IResultPageAlertProps } from '../../../../../../findcare/common/interfaces/iPageAlert';
import { IProvider, IProviderList } from '../../../../../result/interfaces/iProvider';
import { Not_Available } from '../../../../constants/common';
import { PageAlertID } from '../../../../enums/pageAlertId';
import { IMemberCriteria } from '../../../../interfaces/iMemberCriteria';
import { CommonUtility } from '../../../../utilities/commonUtil';
import { ContractUtility } from '../../../../utilities/contractUtil';
import { CustomSpecialtyMatchingUtility } from '../../../../utilities/customSpecialtyMatchingUtil';

export class PageAlertHandler {
  static buildPageAlertRequest(pageType: FindCarePageType, appSession: AppSession, providerTaxonomies?: string[]): IPageAlertRequest {
    const request: IPageAlertRequest = {
      page: pageType
    };
    //Brands
    if (appSession?.metaData?.brandCd) {
      request.brand = [appSession.metaData.brandCd];
    }
    //prefix
    const prefix = this.getPrefix(appSession.searchParams, appSession)?.toLocaleUpperCase();
    if (prefix) {
      request.prefix = [prefix];
    }
    //searhState
    request.searchState = appSession.searchParams && appSession.searchParams.stateCd && appSession.searchParams?.stateCd?.code ? [appSession.searchParams.stateCd.code] : [];
    if (appSession?.searchParams) {
      request.specialties = appSession.searchParams.specialtySelectNm || [];
      request.taxonomies = pageType === FindCarePageType.DetailOverview ? (providerTaxonomies ?? []) : (appSession.searchParams.taxonomySelectNm ?? []);
      request.provTypeCodes = this.getProviderType(appSession);
    }

    //state - plan state code / prefix state code /
    request.state = appSession.searchParams?.plan && appSession.searchParams?.plan.stateCd ? [appSession.searchParams?.plan?.stateCd] : [appSession.alphaPrefixStateCode];
    if (appSession?.searchParams?.plan?.isNationalPlan) {
      request.state.push('NT');
    }
    request.mbu = appSession.searchParams?.plan?.category?.catName ? [appSession.searchParams.plan.category.catName] : [];
    //plan category
    if (appSession?.searchParams?.plan?.category?.catCode) {
      request.planCategory = [appSession?.searchParams?.plan?.category?.catCode];
    }

    //member info
    if (appSession.appState?.executionMode === SearchExecutionMode.SECURE) {
      const selectedContract = appSession.appState?.selectedEligibilityProduct?.selectedContract;
      request.memberCriteria = {};
      request.memberCriteria.mbrUid = selectedContract?.mbrUid;
      request.memberCriteria.contractUid = selectedContract.contractUid;
    }
    //hcid-prefix
    const hcidPrefix = appSession.getHcidPrefix();
    if (hcidPrefix) {
      request.hcidPrefix = [appSession.hcidPrefix];
    }
    return request;
  }

  static getMemberInfo(appSession: AppSession): IMemberCriteria | undefined {
    const member: IMemberCriteria = {};
    return member;
  }

  static getPrefix(searchParams: ISearchParameters, appSession: AppSession): string | undefined {
    if (searchParams?.plan?.alphaPrefix) {
      return searchParams.plan.alphaPrefix;
    } else if (appSession?.searchParams?.plan?.alphaPrefix) {
      return appSession?.searchParams.plan.alphaPrefix;
    } else if (appSession?.deeplinkParams?.alphaprefix) {
      return appSession.deeplinkParams.alphaprefix;
    }
    return undefined;
  }

  static buildPageAlertMessage(alertID: IPageAlertResponse, appSession: AppSession, pageName: FindCarePageType) {
    const upcomingPageAlertIds: string[] = alertID.pageAlerts ?? [];
    appSession.pageAlerts[pageName] = [];
    this.updateNewPageAlerts(this.alertsToBeSkipped(upcomingPageAlertIds, pageName, appSession), pageName, appSession, false);
    CommonUtility.displayStaticAlerts(appSession, pageName);
  }

  // TODO: this functon will be removed in future
  static buildStaticPageAlerts(alertIds: string[], appSession: AppSession, pageName: FindCarePageType) {
    if (appSession.pageAlerts[pageName].length === 0) {
      this.updateNewPageAlerts(alertIds, pageName, appSession, true);
    } else {
      const newAlertIds = alertIds.filter((id) => !appSession.pageAlerts[pageName].map((alert) => alert.id).includes(id));
      if (newAlertIds.length > 0) {
        this.updateNewPageAlerts(newAlertIds, pageName, appSession, true);
      }
    }
  }

  static removePageAlert(alertIds: string[], appSession: AppSession, pageName: FindCarePageType) {
    // Get the page alerts for the specified page
    const pageAlert = appSession.pageAlerts[pageName];

    // Filter out the alerts that match the alertIds to be removed
    const updatedPageAlert = pageAlert.filter((alert) => !alertIds.includes(alert.id));

    // Update the appSession with the filtered alerts
    appSession.pageAlerts[pageName] = updatedPageAlert;
  }

  static updateNewPageAlerts(alertID: string[], page: FindCarePageType, appSession: AppSession, isStatic: boolean = false) {
    const isBHAlertIndex = appSession.pageAlerts[page].findIndex((alert) => alert['id'] === PageAlertID.PA_BH_SEARCH_ASSISTANT);
    if (isBHAlertIndex !== -1) {
      appSession.pageAlerts[page][isBHAlertIndex]['sequence'] = '1';
    }
    appSession.pageAlerts[page].unshift(
      ...alertID.map((id) => {
        return {
          sequence: isBHAlertIndex === -1 && isStatic ? '1' : '2',
          type: PAGE_ALERT_TYPE[id] ?? PageAlertType.INFORMATION,
          id: id
        };
      })
    );
    appSession.pageAlerts[page].sort((a, b) => parseInt(a.sequence) - parseInt(b.sequence));
  }

  static alertsToBeSkipped(alertID: string[], page: FindCarePageType, appSession: AppSession): string[] {
    const matchedIds = alertID.filter((id) => ALERTS_TO_BE_SUPPRESSED.includes(id));
    // reset skippedPageAlerts on each call
    appSession.skippedPageAlerts = [];
    if (matchedIds.length > 0) {
      // Add matched ids to appSession.skippedPageAlerts
      appSession.skippedPageAlerts.push(...matchedIds);
      // Remove matched ids from alertID
      alertID = alertID.filter((id) => !ALERTS_TO_BE_SUPPRESSED.includes(id));
    }
    return alertID;
  }

  static getProviderType(appSession: AppSession): string[] {
    if (
      appSession.searchParams?.providerTypeCodeList?.length &&
      (appSession.searchParams.currentScenario === SEARCH_SCENARIOS.PUBLIC_V1_SPECIALTY ||
        appSession.searchParams.currentScenario === SEARCH_SCENARIOS.SECURE_V2_RX_SPECIALTY ||
        appSession.searchParams.currentScenario === SEARCH_SCENARIOS.SECURE_V1_SPECIALTY)
    ) {
      appSession.searchParams.currentScenario = '';
      return appSession.searchParams?.providerTypeCodeList;
    } else if (appSession.searchParams?.typeSelectNm) {
      return [appSession.searchParams.typeSelectNm];
    } else if (appSession.providerTypeCodes?.length > 0) {
      return appSession.providerTypeCodes;
    } else {
      return [];
    }
  }

  static replaceLiteralwithContent(id: string, providersInfo: IProviderList, appSession, content): string {
    const _provName = appSession.searchTerm ? appSession.searchTerm.trim() : '';
    let _distance = appSession?.searchParams?.distanceNm;
    let alertMessage = content.common.pageAlerts[id] ?? id;

    if (/{deeplinkid}/gi.test(alertMessage)) {
      let deeplinkUrl = CommonUtility.getProcedureDeeplinkUrl(id, appSession);
      const deeplinkId = `${DEEP_LINK_ID}${DOT_OPERAND}${deeplinkUrl ? deeplinkUrl : ''}${AMPERSAND}${DEEP_LINK_CMP}`;
      alertMessage = alertMessage.replace(/{deeplinkid}/gi, deeplinkId);
    }

    if (/{RDS}/gi.test(alertMessage)) {
      alertMessage = alertMessage.replace(/{RDS}/gi, providersInfo?.criteriaChange?.increasedRadius ?? _distance);
    }
    if (/{LOC}/gi.test(alertMessage)) {
      alertMessage = alertMessage.replace(/{LOC}/gi, ResultsUtil.getLocationText(appSession));
    }
    if (/{CNT}/gi.test(alertMessage)) {
      alertMessage = alertMessage.replace(/{CNT}/gi, providersInfo?.totalRecords ?? 0);
    }
    if (/{PVD}/gi.test(alertMessage)) {
      alertMessage = alertMessage.replace(/{PVD}/gi, this.getProviderTypeText(appSession, content));
    }
    if (/{PLAN}/gi.test(alertMessage)) {
      alertMessage = alertMessage.replace(/{PLAN}/gi, CommonUtility.getPlanNetworkPrefixText(appSession, content));
    }
    if (/{MATCH}/gi.test(alertMessage)) {
      alertMessage = alertMessage.replace(/{MATCH}/gi, _provName && _provName.length > 0 ? content?.result?.summaryComponent.resultsHeader.matchingNew.replace(/{NAM}/gi, _provName) : '');
    }
    if (/{REFINE}/gi.test(alertMessage)) {
      alertMessage = alertMessage.replace(/{REFINE}/gi, content.result.summaryComponent.resultsHeader.refineResultsNew);
    }
    if (/{brandUrl}/gi.test(alertMessage)) {
      alertMessage = this.replaceBrnadsInAlert(alertMessage, appSession, ALERT_CUSTOMER_SUPPORT_BRAND_URL_CONFIG[id]);
    }
    if (/{VISITS_VALUE}/gi.test(alertMessage) && providersInfo && providersInfo.providers) {
      alertMessage = alertMessage.replace(/{VISITS_VALUE}/gi, providersInfo.providers[0]?.eycCostInfo?.noOfVisitsVal ?? '');
    }
    if (/{loginUrl}/gi.test(alertMessage)) {
      alertMessage = alertMessage.replace(/{loginUrl}/gi, appSession.loginUrl);
    }
    if (/{billingCode}/gi.test(alertMessage)) {
      const bariaticAlerts = [PageAlertID.PA_BARIATRIC_KNEE_ALERT_PHONE_WEB.toString(), PageAlertID.PA_BARIATRIC_KNEE_ALERT_WEB.toString(), PageAlertID.PA_BARIATRIC_KNEE_ALERT_PHONE.toString()];
      if (appSession.pageAlerts.results.some((alert) => bariaticAlerts.includes(alert.id))) {
        alertMessage = alertMessage.replace(/{billingCode}/gi, appSession.billingType + '-' + appSession.medicalCode);
      } else if (appSession.pageAlerts.detailsCostEstimate.some((alert) => bariaticAlerts.includes(alert.id))) {
        alertMessage = alertMessage.replace(/{billingCode}/gi, appSession.selectedCptInfo?.billingType + '-' + appSession.selectedCptInfo?.medicalCode?.code);
      }
      alertMessage = alertMessage.replace(/{vendorName}/gi, appSession.vendorDetails?.vendorName);
      alertMessage = alertMessage.replace(/{vendorWebsite}/gi, appSession.vendorDetails?.url);
      alertMessage = alertMessage.replace(/{vendorPhone}/gi, appSession.vendorDetails?.vendorPhno);
    }
    return alertMessage;
  }

  static replaceBrnadsInAlert(alertMessage: string, appSession: AppSession, alertBrandContent): string {
    const brandCd = appSession?.metaData?.brandCd;
    const locale = appSession?.metaData?.locale;
    const urlToBeReplaced = alertBrandContent[locale][brandCd] ?? alertBrandContent[locale]['ABCBS'];
    if (brandCd && locale && alertBrandContent && urlToBeReplaced) {
      alertMessage = alertMessage.replace(/{brandUrl}/gi, urlToBeReplaced);
    }
    return alertMessage;
  }

  static getProviderTypeText(_appSession: AppSession, content: any) {
    const defaultName = content.result.summaryComponent.searchCriteriaComponent.labels.careProviders;

    if (!_appSession.searchParams) {
      return defaultName;
    }

    const { coverageTypeCode, providerTypeCodeList } = _appSession.searchParams;
    const typeSelected = providerTypeCodeList?.[0];

    if (!typeSelected) {
      return defaultName;
    }

    const providerTypes = [PROVIDER_TYPE.DENTAL.toString(), PROVIDER_TYPE.VISION.toString()];
    const resultType = content.result?.summaryComponent.resultsProvType[typeSelected + coverageTypeCode];

    if (providerTypes.includes(typeSelected) && coverageTypeCode === MEDICAL_COVERAGETYPE && resultType) {
      return resultType;
    }

    const providers = content.commonHealthCareContent?.providers ?? [];
    const type = providers.find((x) => x.code === typeSelected);

    if (type?.name && !ResultsUtil.isDentalSearch(_appSession)) {
      return type.name;
    } else if (ResultsUtil.isDentalSearch(_appSession)) {
      return content.summaryComponent.resultsProvType[PROVIDER_TYPE.DENTAL];
    } else {
      return content.result?.summaryComponent.resultsProvType[typeSelected] || defaultName;
    }
  }

  static getIconStyle(type: string): string {
    switch (type) {
      case PageAlertType.INFORMATION:
        return PageAlertTypeStyle.INFORMATION;
      case PageAlertType.WARNING:
        return PageAlertTypeStyle.WARNING;
      case PageAlertType.NEGATIVE:
        return PageAlertTypeStyle.NEGATIVE;
      default:
        return '';
    }
  }

  /****************************
   ****************************
   *** SEARCH RESULT ALERTS ***
   ****************************
   ****************************/
  static handlePageAlerts(props: IResultPageAlertProps) {
    const { response, providers, appSession, currentScenario, isCptSearch, isColdStateEYC, isOONSearch, showCptAlert, isEyc, isOfficeVisitCostEnabled, isMedicalGroupSearch } = props;

    PageAlertHandler.handleVisitAlerts(providers, appSession);
    PageAlertHandler.handleScenarioAlerts(currentScenario, appSession);
    PageAlertHandler.handlePrimeGroupSearch(appSession, isMedicalGroupSearch || false);
    PageAlertHandler.handleCptAlerts(showCptAlert, providers, isCptSearch, appSession);
    PageAlertHandler.handleBacktoPlanSelectionAlert(response, providers, appSession, isColdStateEYC);
    PageAlertHandler.handleProviderCostAlerts(providers, appSession, currentScenario, isEyc, isOfficeVisitCostEnabled);
    PageAlertHandler.handleSNYAlert(providers, appSession);
    PageAlertHandler.handleVisionProcedureOrMedicalSearchAlert(appSession, providers);
    PageAlertHandler.handleSpecialtySearchAlert(appSession, providers);
    PageAlertHandler.handleBehavioralHealthAlert(providers, appSession);
  }

  static handleVisitAlerts(providers: IProvider[], appSession: AppSession) {
    if ((providers || []).some((prov) => prov?.eycCostInfo?.noOfVisits === VISITS.SUBSEQUENT)) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_MORE_THAN_ONE_VISIT_COST_MESSAGE], appSession, FindCarePageType.Results);
    } else {
      PageAlertHandler.removePageAlert([PageAlertID.PA_MORE_THAN_ONE_VISIT_COST_MESSAGE], appSession, FindCarePageType.Results);
    }

    if ((providers || []).some((prov) => prov?.eycCostInfo?.noOfVisits === VISITS.INITIAL)) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_INITIAL_VISIT_COST_MESSAGE], appSession, FindCarePageType.Results);
    } else {
      PageAlertHandler.removePageAlert([PageAlertID.PA_INITIAL_VISIT_COST_MESSAGE], appSession, FindCarePageType.Results);
    }
  }

  static handleScenarioAlerts(currentScenario: PRECARE_SEARCH_SCENARIO, appSession: AppSession) {
    if (currentScenario === SEARCH_SCENARIOS.SECURE_V1_TCC_DENTAL) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_DENTAL_PROC_MSG], appSession, FindCarePageType.Results);
    }
  }

  static handlePrimeGroupSearch(appSession: AppSession, isMedicalGroupSearch: boolean) {
    if (isMedicalGroupSearch) {
      const alertId = appSession.hasHealthSystem ? PageAlertID.PA_HEALTH_SYSTEM_MSG : PageAlertID.PA_PRIME_GROUP_SEARCH;
      PageAlertHandler.buildStaticPageAlerts([alertId], appSession, FindCarePageType.Results);
    } else {
      PageAlertHandler.removePageAlert([PageAlertID.PA_PRIME_GROUP_SEARCH, PageAlertID.PA_HEALTH_SYSTEM_MSG], appSession, FindCarePageType.Results);
    }
  }

  static handleCptAlerts(showCptAlert: boolean, providers: IProvider[], isCptSearch: boolean, appSession: AppSession) {
    if (showCptAlert && providers?.length > 0) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_CPT_ALERT], appSession, FindCarePageType.Results);
    }
  }

  static handleSNYAlert(providers: IProvider[], appSession: AppSession) {
    if (appSession.feature?.isSNYMember && providers.length === 0) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_NO_RESULT_SNY_MEMBER], appSession, FindCarePageType.Results);
    } else {
      PageAlertHandler.removePageAlert([PageAlertID.PA_NO_RESULT_SNY_MEMBER], appSession, FindCarePageType.Results);
    }
  }

  static handleBacktoPlanSelectionAlert(summaryResponse: IProviderList, providers: IProvider[], appSession: AppSession, isColdStateEYC: boolean) {
    if (appSession.isEyc && providers && summaryResponse && !summaryResponse?.costRange && isColdStateEYC) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_PLAN_SELECTION], appSession, FindCarePageType.Results);
    }
  }

  static handleProviderCostAlerts(providers: IProvider[], appSession: AppSession, currentScenario: PRECARE_SEARCH_SCENARIO, isEyc: boolean, isOfficeVisitCostEnabled: boolean) {
    let showAlert = false;

    if (providers?.length) {
      if (currentScenario === SEARCH_SCENARIOS.SECURE_V1_CPT_MEDICAL && CommonUtility.isCptSearch(appSession)) {
        // Check if any provider is missing cpt costInfo.memberResponsibility
        showAlert = providers.some((provider) => !provider?.costInfo || provider?.costInfo?.memberResponsibility === Not_Available);
      } else if (currentScenario === SEARCH_SCENARIOS.SECURE_V1_TCC_MEDICAL && isEyc) {
        // Check if any provider is missing procidure eycCostInfo.totalYouPay
        showAlert = providers.some((provider) => !provider.eycCostInfo || provider.eycCostInfo.totalYouPay === undefined);
      }
    }

    if (showAlert) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_COST_NOT_AVAILABLE_ALERT], appSession, FindCarePageType.Results);
    }
  }

  static handleVisionProcedureOrMedicalSearchAlert(appSession: AppSession, providers: IProvider[]): void {
    const { tccCodes, medicalCodes } = appSession?.bootstrap?.appConfMgr?.staticPageAlertConf?.visionSearchAlert ?? {};
    const hasVisionCareCodes = medicalCodes?.includes(appSession?.medicalCode) || tccCodes?.includes(appSession?.searchParams?.eycSearchParam?.eycProcedureCriteria?.procedureCode);

    if (providers?.length && ContractUtility.hasEmbeddedVisionCoverage(appSession) && hasVisionCareCodes) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_VISION_PROCEDURE_MEDICAL_SEARCH_ALERT], appSession, FindCarePageType.Results);
    }
  }

  static handleSpecialtySearchAlert(appSession: AppSession, providers: IProvider[]): void {
    if (providers?.length) {
      const specialtyConfig = CustomSpecialtyMatchingUtility.getSpecialtyMatchingConfig(appSession);
      if (
        !isEmpty(specialtyConfig) &&
        specialtyConfig.networkType === NETWORK_TYPE.VISION &&
        ContractUtility.hasEmbeddedVisionCoverage(appSession) &&
        appSession.searchParams?.planTypeList?.length === 1 &&
        appSession.searchParams.planTypeList[0] === specialtyConfig.networkType
      ) {
        PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_VISION_SPECIALTY_SEARCH_ALERT], appSession, FindCarePageType.Results);
      }

      if (
        !isEmpty(specialtyConfig) &&
        specialtyConfig.networkType === NETWORK_TYPE.DENTAL &&
        ContractUtility.hasEmbeddedDentalCoverage(appSession) &&
        appSession.searchParams?.planTypeList?.length === 1 &&
        appSession.searchParams?.planTypeList[0] === specialtyConfig.networkType
      ) {
        PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_DENTAL_SPECIALTY_SEARCH_ALERT], appSession, FindCarePageType.Results);
      }
    }
  }

  static handleBehavioralHealthAlert(providers: IProvider[], appSession: AppSession) {
    const showNyBhAlert = !providers || providers.length === 0 || providers.some((obj) => obj.providerTypeCodeList?.includes('B'));

    if (appSession.skippedPageAlerts && appSession.skippedPageAlerts.some((alertId) => alertId === PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_PUBLIC && showNyBhAlert)) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_PUBLIC], appSession, FindCarePageType.Results);
    } else {
      PageAlertHandler.removePageAlert([PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_PUBLIC], appSession, FindCarePageType.Results);
    }

    if (appSession.skippedPageAlerts && appSession.skippedPageAlerts.some((alertId) => alertId === PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_SECURE) && showNyBhAlert) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_SECURE], appSession, FindCarePageType.Results);
    } else {
      PageAlertHandler.removePageAlert([PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_SECURE], appSession, FindCarePageType.Results);
    }

    if (appSession.skippedPageAlerts && appSession.skippedPageAlerts.some((alertId) => alertId === PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_CARE_COMPLAINT) && showNyBhAlert) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_CARE_COMPLAINT], appSession, FindCarePageType.Results);
    } else {
      PageAlertHandler.removePageAlert([PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_CARE_COMPLAINT], appSession, FindCarePageType.Results);
    }

    if (appSession.skippedPageAlerts && appSession.skippedPageAlerts.some((alertId) => alertId === PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_APPOINTMENT) && showNyBhAlert) {
      PageAlertHandler.buildStaticPageAlerts([PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_APPOINTMENT], appSession, FindCarePageType.Results);
    } else {
      PageAlertHandler.removePageAlert([PageAlertID.PA_NY_COMMERCIAL_BH_PROVIDERS_APPOINTMENT], appSession, FindCarePageType.Results);
    }
  }
}
